/** 기존 @gc/common/misc - FE용 유틸 */

/** 0으로 나누기 방지. 값이 없으면 `fallback`(기본 0) — 차트 데이터처럼 `number`가
 *  보장돼야 하는 곳에서 쓴다. "데이터 없음"을 구분해야 하는 표 셀에는 `safeRatio`. */
export function safeDiv(a?: number | null, b?: number | null, fallback = 0): number {
  if (a == null || b == null || !b) return fallback;
  return a / b;
}

/** `safeDiv`와 같은 가드지만 값이 없으면 `undefined` — "데이터 없음"을 0과 구분해
 *  '-'로 렌더해야 하는 표 셀용(조회당 비용, CTR 등). */
export function safeRatio(a?: number | null, b?: number | null): number | undefined {
  if (!a || !b) return undefined;
  return a / b;
}

/** trailing zero + 남은 소수점 제거 ("0.070" → "0.07", "2.000" → "2"). */
function trimTrailingZeros(s: string): string {
  return s.includes('.') ? s.replace(/0+$/, '').replace(/\.$/, '') : s;
}

/**
 * 소액 단가 포맷 (조회당/클릭당 비용, CTR 등): 소수 3자리까지 + trailing zero 제거.
 * 0.001 미만은 유효숫자가 다 잘리므로 6자리까지 표시한다.
 *
 * 같은 성격의 지표를 페이지마다 다른 자릿수·빈값 표기로 보여주던 것을 통일한 단일
 * 정의다(프로필 스탯 기준). 빈값은 기본 '-'이며, 0을 표시해야 하는 호출부만
 * `{ fallback: '0' }`으로 명시한다.
 */
export function smallNumber(n?: number | null, opts?: { fallback?: string }): string {
  const { fallback = '-' } = opts ?? {};
  if (n == null || !Number.isFinite(n)) return fallback;
  if (n === 0) return '0';
  if (Math.abs(n) < 0.001) return trimTrailingZeros(n.toFixed(6));
  return trimTrailingZeros(n.toFixed(3));
}

/**
 * snake_case/kebab-case 식별자를 Title Case 라벨로 (`content_creation` →
 * `Content Creation`). 상태·타입 enum을 화면에 그대로 노출할 때의 공용 포맷터 —
 * 여러 페이지에 같은 정규식이 인라인으로 흩어져 있던 것을 하나로 모았다.
 */
export function formatLabelText(value?: string | null): string {
  if (!value) return '-';
  return value
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * 숫자를 K/M/B 약어로 포맷.
 * abbreviate=false이면 toLocaleString() 그대로 반환.
 */
export function formatAbbrev(n: number, abbreviate = true): string {
  if (!Number.isFinite(n)) return '-';
  if (abbreviate) {
    if (Math.abs(n) >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
    if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  }
  return n.toLocaleString();
}

/**
 * niceNumber: null/undefined → '-', 그 외 → formatAbbrev
 */
export function niceNumber(n: number | null | undefined, abbreviate = false): string {
  if (n == null || !Number.isFinite(n)) return '-';
  return formatAbbrev(n, abbreviate);
}

/** USD 통화 포맷 ("$" + toLocaleString). null/undefined → '-'. */
export function formatCurrency(n?: number | null, options?: Intl.NumberFormatOptions): string {
  return n != null ? `$${n.toLocaleString(undefined, options)}` : '-';
}

/** K/M/B 약어 포맷의 한 단계 (예: { min: 1e6, divisor: 1e6, suffix: 'M', decimals: 1 }). */
export interface CompactNumberTier {
  min: number;
  divisor: number;
  suffix: string;
  decimals: number;
}

/** 기본 tier: M(소수 1자리) → K(소수 1자리). B 단위 없음. */
const DEFAULT_COMPACT_TIERS: CompactNumberTier[] = [
  { min: 1_000_000, divisor: 1_000_000, suffix: 'M', decimals: 1 },
  { min: 1_000, divisor: 1_000, suffix: 'K', decimals: 1 },
];

/**
 * 숫자를 K/M/B 약어로 포맷. 페이지마다 다른 fallback·음수 처리·자릿수·B단위 유무를
 * 옵션으로 흡수한다. tier를 배열로 받으므로(내림차순 min) 자릿수가 구간별로 다르거나
 * 서브 threshold가 있는 경우(예: 1K~10K는 소수 1자리, 10K~1M은 정수)도 표현 가능.
 */
export function formatCompactNumber(
  n: number | null | undefined,
  opts?: {
    /** null/undefined/비유한값(및 rejectNegative 시 음수)에 반환. 기본 '-'. */
    fallback?: string;
    /** true면 음수도 fallback 처리. 기본 false. */
    rejectNegative?: boolean;
    /** true면 falsy(0 포함) 전부 fallback 처리 — `!n` 체크와 동일한 의미. 기본 false. */
    emptyForFalsy?: boolean;
    /** true면 tier 판정에 Math.abs() 사용(음수도 약어 처리, 부호는 결과에 유지). 기본 false. */
    useAbs?: boolean;
    /** 적용할 tier 목록(내림차순 min). 기본 M/K 2단계. */
    tiers?: CompactNumberTier[];
    /** 약어 값의 trailing ".0" 제거 (2.0M → 2M). 기본 false. */
    trimTrailingZero?: boolean;
    /** 1000 미만 값 처리 방식: 'locale'(toLocaleString, 기본) | 'round'(반올림 정수) | 'raw'(String 그대로). */
    belowThousand?: 'locale' | 'round' | 'raw';
  },
): string {
  const {
    fallback = '-',
    rejectNegative = false,
    emptyForFalsy = false,
    useAbs = false,
    tiers = DEFAULT_COMPACT_TIERS,
    trimTrailingZero = false,
    belowThousand = 'locale',
  } = opts ?? {};

  if (emptyForFalsy && !n) return fallback;
  if (n == null || !Number.isFinite(n)) return fallback;
  if (rejectNegative && n < 0) return fallback;

  const check = useAbs ? Math.abs(n) : n;
  for (const tier of tiers) {
    if (check >= tier.min) {
      let s = (n / tier.divisor).toFixed(tier.decimals);
      if (trimTrailingZero && s.endsWith('.0')) s = s.slice(0, -2);
      return `${s}${tier.suffix}`;
    }
  }
  if (belowThousand === 'round') return String(Math.round(n));
  if (belowThousand === 'raw') return String(n);
  return n.toLocaleString();
}

/** 팔로워/구독자 수 축약 (M: 소수 1자리, K: 정수, 0/falsy → ''). */
export function formatFollowerCount(n?: number): string {
  return formatCompactNumber(n, {
    fallback: '',
    emptyForFalsy: true,
    belowThousand: 'raw',
    tiers: [
      { min: 1_000_000, divisor: 1_000_000, suffix: 'M', decimals: 1 },
      { min: 1_000, divisor: 1_000, suffix: 'K', decimals: 0 },
    ],
  });
}

/** 상태 색상 맵에서 알 수 없는 상태값에 쓰는 기본 회색 (payments 페이지 공통) */
export const DEFAULT_STATUS_COLOR = { bg: '#f0f0f0', color: '#888' };

/** 정수 콤마 포맷. round=true(기본)면 반올림 후 콤마, false면 그대로 콤마. null/NaN → fallback. */
export function formatIntComma(n?: number | null, opts?: { fallback?: string; round?: boolean }): string {
  const { fallback = '-', round = true } = opts ?? {};
  if (n == null || Number.isNaN(n)) return fallback;
  return (round ? Math.round(n) : n).toLocaleString('en-US');
}

/**
 * Math.ceil(price * commissionRate), corrected for floating-point representation
 * noise (e.g. `50000 * 1.1 === 55000.00000000001` in JS, which plain Math.ceil
 * would round up to 55001 instead of the mathematically correct 55000).
 */
export function ceilWithCommission(price: number, commissionRate: number): number {
  const raw = price * commissionRate;
  return Math.ceil(Math.round(raw * 1e6) / 1e6);
}
